import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const BORDER_COLOR = "FFCCCCCC";
const HEADER_BG   = "FF2F75B5";
const ZEBRA_LIGHT = "FFF2F2F2";
const ZEBRA_DARK  = "FFFFFFFF";
const TITLE_COLOR = "FF2F75B5";
const TITLE_TEXT = "Yuva Sangathan";

const border = () => ({
  top:    { style: "thin", color: { argb: BORDER_COLOR } },
  bottom: { style: "thin", color: { argb: BORDER_COLOR } },
  left:   { style: "thin", color: { argb: BORDER_COLOR } },
  right:  { style: "thin", color: { argb: BORDER_COLOR } },
});

/** ─── VILLAGE-LEVEL EXPORT ─────────────────────────────────────────────────
 *  Row 1 : Merged title (A:C)
 *  Row 2 : Headers  → Sr No | Village Name | Total Students
 *  Row 3+: Data
 */
export const exportYuvaSangathanVillageExcel = async (filteredData) => {
  if (!filteredData?.length) return;

  const workbook  = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Members");

  const headers = ["Sr No", "Village Name", "Total Students"];
  const totalCols = headers.length;

  // --- Row 1: Title ---
  const titleRow = worksheet.addRow([TITLE_TEXT]);
  worksheet.mergeCells(1, 1, 1, totalCols);
  titleRow.height = 30;
  titleRow.getCell(1).font      = { bold: true, size: 18, color: { argb: TITLE_COLOR } };
  titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
  titleRow.getCell(1).border    = border();

  // --- Row 2: Headers ---
  const headerRow = worksheet.addRow(headers);
  headerRow.height = 22;
  headerRow.eachCell((cell) => {
    cell.font      = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_BG } };
    cell.border    = border();
  });

  // Freeze title + header
  worksheet.views = [{ state: "frozen", ySplit: 2 }];

  // AutoFilter on header row (row 2)
  worksheet.autoFilter = { from: { row: 2, column: 1 }, to: { row: 2, column: totalCols } };

  // --- Row 3+: Data ---
  filteredData.forEach((item, index) => {
    const isZebra = index % 2 === 0;
    const row = worksheet.addRow([
      index + 1,
      item?.Village || "",
      item?.TotalMember || item?.MemberCount || "",
    ]);
    row.height = 32;
    row.eachCell((cell, colIdx) => {
      cell.alignment = {
        vertical: "middle",
        horizontal: colIdx === 1 ? "center" : colIdx === 3 ? "right" : "left",
        wrapText: true,
      };
      cell.fill   = { type: "pattern", pattern: "solid", fgColor: { argb: isZebra ? ZEBRA_LIGHT : ZEBRA_DARK } };
      cell.border = border();
    });
  });

  // Column widths
  worksheet.getColumn(1).width = 8;   // Sr No
  worksheet.getColumn(2).width = 40;  // Village Name
  worksheet.getColumn(3).width = 40;  // Total Students

  const currentDate = new Date().toISOString().split("T")[0];
  const buffer = await workbook.xlsx.writeBuffer();
  const blob   = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  saveAs(blob, `Yuva_Sangathan_Villages_${currentDate}.xlsx`);
};

/** ─── MEMBER-LEVEL EXPORT ──────────────────────────────────────────────────
 *  Row 1 : Merged title  "...સાધના) - VillageName"  (A:F)
 *  Row 2 : Headers  → SR NO | STUDENT ID | STUDENT NAME | INCOME | STUDENT CASTE | YEAR
 *  Row 3+: Data
 */
export const exportYuvaSangathanMemberExcel = async (notificationData, villageName) => {
  if (!notificationData?.length) return;

  const workbook  = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Members");

  const title   = `${TITLE_TEXT} - ${villageName}`;
  const headers = ["SR NO", "TEAM NAME", "VILLAGE", "CAPTAIN", "CONTACT NO"];
  const totalCols = headers.length;

  // --- Row 1: Title ---
  const titleRow = worksheet.addRow([title]);
  worksheet.mergeCells(1, 1, 1, totalCols);
  titleRow.height = 30;
  titleRow.getCell(1).font      = { bold: true, size: 14, color: { argb: TITLE_COLOR } };
  titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
  titleRow.getCell(1).border    = border();

  // --- Row 2: Headers ---
  const headerRow = worksheet.addRow(headers);
  headerRow.height = 22;
  headerRow.eachCell((cell) => {
    cell.font      = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_BG } };
    cell.border    = border();
  });

  // Freeze title + header
  worksheet.views = [{ state: "frozen", ySplit: 2 }];

  // AutoFilter on header row (row 2)
  worksheet.autoFilter = { from: { row: 2, column: 1 }, to: { row: 2, column: totalCols } };

  // --- Row 3+: Data ---
  notificationData.forEach((item, index) => {
    const isZebra = index % 2 === 0;
    const row = worksheet.addRow([
      index + 1,
      item?.SHGName || "-",
      item?.GramPanchayat || "-",
      item?.MemberName || "-",
      item?.MobileNo || "-",
    ]);
    row.height = 32;
    row.eachCell((cell, colIdx) => {
      cell.alignment = {
        vertical: "middle",
        horizontal: colIdx === 1 ? "center" : "left",
        wrapText: true,
      };
      cell.fill   = { type: "pattern", pattern: "solid", fgColor: { argb: isZebra ? ZEBRA_LIGHT : ZEBRA_DARK } };
      cell.border = border();
    });
  });

  // Column widths
  worksheet.getColumn(1).width = 8;   // SR NO
  worksheet.getColumn(2).width = 42;  // TEAM NAME
  worksheet.getColumn(3).width = 25;  // VILLAGE
  worksheet.getColumn(4).width = 32;  // CAPTAIN
  worksheet.getColumn(5).width = 18;  // CONTACT NO

  const currentDate = new Date().toISOString().split("T")[0];
  const buffer = await workbook.xlsx.writeBuffer();
  const blob   = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  saveAs(blob, `Yuva_Sangathan_Members_${currentDate}.xlsx`);
};
